/**
 * Feedback Modal Komponente
 * Implementiert das Modal zum Erfassen zusätzlichen Feedbacks nach einer Bewertung
 */
import { FEEDBACK_DELAY_MS } from '../utils/constants.js'

// Modal-DOM-Struktur für hohe Bewertung (4-5 Sterne)
const HIGH_RATING_MODAL_HTML = `
<div class="rw-modal-backdrop">
    <div class="rw-modal-container">
        <div class="rw-modal-header">
            <h3 class="rw-modal-title"></h3>
        </div>
        <div class="rw-modal-body">
            <div class="rw-feedback-modal-content">
            <div class="bodytext">
                <p class="rw-feedback-description"></p>
            </div>
                <form class="rw-feedback-form">
                    <div class="rw-input-group">
                        <input type="email" id="rw-feedback-email" class="rw-feedback-email" placeholder="" />
                    </div>
                    <div class="rw-privacy-text"></div>
                    <div class="rw-feedback-buttons">
                        <button type="button" class="rw-feedback-cancel-button btn"></button>
                        <button type="submit" class="rw-feedback-submit-button btn"></button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</div>
`

// Modal-DOM-Struktur für niedrige Bewertung (1-3 Sterne)
const LOW_RATING_MODAL_HTML = `
<div class="rw-modal-backdrop">
    <div class="rw-modal-container">
        <div class="rw-modal-header">
            <h3 class="rw-modal-title"></h3>
        </div>
        <div class="rw-modal-body">
            <div class="rw-feedback-modal-content">
                <div class="bodytext">
                    <p class="rw-feedback-description"></p>
                </div>
                <form class="rw-feedback-form">
                    <div class="rw-input-group">
                        <label class="rw-feedback-label" for="rw-feedback-input"></label>
                        <textarea id="rw-feedback-input" class="rw-feedback-input" rows="4"></textarea>
                    </div>
                    <div class="rw-feedback-buttons">
                    <button type="button" class="rw-feedback-cancel-button btn"></button>
                        <button type="submit" class="rw-feedback-submit-button btn"></button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</div>
`

export class FeedbackModal {
    constructor(options = {}) {
        this.options = {
            ratingPid: options.ratingPid,
            ratingValue: options.ratingValue || 0,
            linkUrl: options.linkUrl || '',
            onSubmit: options.onSubmit || null,
            translations: options.translations || {
                modalTitle: '',
                submitButton: '',
                cancelButton: '',
                header: '',
                description: '',
                inputLabel: '',
                thankYou: '',
                emailLabel: '',
                privacyText: '',
                highRatingThreshold: 4,
            },
        }

        this.feedback = ''
        this.email = ''
        this.modalElement = null
        this.backdropElement = null
        this.feedbackInput = null
        this.emailInput = null
        this.formElement = null
        this.isHighRating = this.options.ratingValue >= (this.options.translations.highRatingThreshold || 4)

        // Modal erstellen und vorbereiten
        this._createModal()
    }

    /**
     * Erstellt das Modal und fügt es dem DOM hinzu
     * @private
     */
    _createModal() {
        // Container für das Modal erstellen basierend auf Rating-Level
        const container = document.createElement('div')
        container.innerHTML = this.isHighRating ? HIGH_RATING_MODAL_HTML.trim() : LOW_RATING_MODAL_HTML.trim()

        // Elemente speichern
        this.backdropElement = container.querySelector('.rw-modal-backdrop')
        this.modalElement = container.querySelector('.rw-modal-container')
        this.formElement = container.querySelector('.rw-feedback-form')

        // Bei hohen Bewertungen das Email-Input einrichten
        if (this.isHighRating) {
            this.emailInput = container.querySelector('#rw-feedback-email')

            // Submit button reference
            this.submitButton = container.querySelector('.rw-feedback-submit-button')
            
            // Initially disable submit button
            if (this.submitButton) {
                this.submitButton.disabled = true
                this.submitButton.classList.add('btn--disabled')
            }
            
            // Email Input Event-Listener
            if (this.emailInput) {
                this.emailInput.addEventListener('input', (e) => {
                    this.email = e.target.value
                    
                    // Enable/disable submit button based on email content
                    this.validateForm()
                })
                // Set placeholder text
                this.emailInput.placeholder = this.options.translations.emailLabel
            }

            // Datenschutztext setzen (nur bei hohen Bewertungen mit E-Mail-Feld)
            const privacyTextElement = this.modalElement.querySelector('.rw-privacy-text')
            if (privacyTextElement) {
                privacyTextElement.innerHTML = this.options.translations.privacyText
            }
        }
        // Bei niedrigen Bewertungen das Feedback-Input einrichten
        else {
            this.feedbackInput = container.querySelector('#rw-feedback-input')

            // Submit button reference
            this.submitButton = container.querySelector('.rw-feedback-submit-button')
            
            // Initially disable submit button
            if (this.submitButton) {
                this.submitButton.disabled = true
                this.submitButton.classList.add('btn--disabled')
            }
            
            // Input Event-Listener
            if (this.feedbackInput) {
                this.feedbackInput.addEventListener('input', (e) => {
                    this.feedback = e.target.value
                    
                    // Enable/disable submit button based on feedback content
                    this.validateForm()
                })
            }

            // Feedback-Label setzen für niedrige Bewertungen
            const labelElement = this.modalElement.querySelector('.rw-feedback-label')
            if (labelElement) {
                labelElement.textContent = this.options.translations.inputLabel
            }
        }

        // Modal-Titel setzen
        const titleElement = this.modalElement.querySelector('.rw-modal-title')
        titleElement.textContent = this.isHighRating
            ? this.options.translations.highTitle
            : this.options.translations.lowTitle

        // Header und Beschreibung setzen
        const descriptionElement = this.modalElement.querySelector('.rw-feedback-description')
        descriptionElement.textContent = this.isHighRating
            ? this.options.translations.highDescription
            : this.options.translations.lowDescription

        // Buttons setzen
        const submitButton = this.modalElement.querySelector('.rw-feedback-submit-button')
        submitButton.textContent = this.options.translations.submitButton

        const cancelButton = this.modalElement.querySelector('.rw-feedback-cancel-button')
        cancelButton.textContent = this.options.translations.cancelButton

        // Form-Submit-Event
        this.formElement.addEventListener('submit', (e) => {
            e.preventDefault()
            this.submitFeedback()
        })

        // Cancel-Button-Event
        cancelButton.addEventListener('click', () => this.close())

        // Close button removed

        // Backdrop-Click-Event
        this.backdropElement.addEventListener('click', (event) => {
            if (event.target === this.backdropElement) {
                this.close()
            }
        })

        // ESC-Taste zum Schließen
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen()) {
                this.close()
            }
        })

        // Dokument-Body hinzufügen
        document.body.appendChild(container.firstChild)

        // Initial ausblenden
        this.backdropElement.style.display = 'none'
    }
    
    /**
     * Validiert das Formular und aktiviert/deaktiviert den Submit-Button
     */
    validateForm() {
        if (!this.submitButton) return;
        
        // Prüfen, ob E-Mail oder Feedback vorhanden ist, je nach Modal-Typ
        let isValid = false;
        
        if (this.isHighRating && this.email) {
            // Für hohe Bewertungen: E-Mail muss vorhanden sein
            isValid = this.email.trim() !== '';
        } else if (!this.isHighRating && this.feedback) {
            // Für niedrige Bewertungen: Feedback muss vorhanden sein
            isValid = this.feedback.trim() !== '';
        }
        
        // Button aktivieren oder deaktivieren
        if (isValid) {
            this.submitButton.disabled = false;
            this.submitButton.classList.remove('btn--disabled');
        } else {
            this.submitButton.disabled = true;
            this.submitButton.classList.add('btn--disabled');
        }
    }

    /**
     * Sendet das Feedback
     */
    submitFeedback() {
        let feedbackData = {
            ratingPid: this.options.ratingPid,
            ratingValue: this.options.ratingValue,
        }

        // Bei hohen Bewertungen nur die Email senden
        if (this.isHighRating && this.emailInput) {
            feedbackData.email = this.email.trim()
        }
        // Bei niedrigen Bewertungen nur das Feedback senden
        else if (!this.isHighRating && this.feedbackInput) {
            feedbackData.feedback = this.feedback.trim()
        }

        // Callback aufrufen, falls vorhanden
        if (typeof this.options.onSubmit === 'function') {
            this.options.onSubmit(feedbackData)
        }

        // Danke-Nachricht anzeigen
        this._showThankYouMessage()

        // Nach kurzer Verzögerung schließen
        setTimeout(() => {
            this.close()
        }, FEEDBACK_DELAY_MS)
    }

    /**
     * Zeigt eine Danke-Nachricht nach dem Absenden
     * @private
     */
    _showThankYouMessage() {
        // Inhalt mit Danke-Nachricht ersetzen
        const content = this.modalElement.querySelector('.rw-feedback-modal-content')
        if (content) {
            // Inhalt leeren
            content.innerHTML = ''

            // Danke-Nachricht hinzufügen
            const thankYou = document.createElement('div')
            thankYou.className = 'rw-feedback-thank-you'
            thankYou.textContent = this.options.translations.thankYou
            content.appendChild(thankYou)
        }
    }

    /**
     * Öffnet das Modal
     * @returns {FeedbackModal} - Sich selbst für Method-Chaining
     */
    open() {
        // Prüfen, ob die Bewertung zwischen hoch/niedrig gewechselt hat
        const shouldBeHighRating = this.options.ratingValue >= (this.options.translations.highRatingThreshold || 4)

        // Wenn sich der Typ geändert hat, Modal neu erstellen
        if (this.isHighRating !== shouldBeHighRating) {
            // Altes Modal entfernen
            if (this.backdropElement && this.backdropElement.parentNode) {
                this.backdropElement.parentNode.removeChild(this.backdropElement)
            }

            // Typ aktualisieren
            this.isHighRating = shouldBeHighRating

            // Modal neu erstellen
            this._createModal()
        }

        // Felder zurücksetzen
        this.feedback = ''
        this.email = ''

        if (this.feedbackInput) {
            this.feedbackInput.value = ''
        }

        if (this.emailInput) {
            this.emailInput.value = ''
        }

        // Modal anzeigen
        this.backdropElement.style.display = 'block'
        this.backdropElement.classList.add('rw-modal-open')
        this.modalElement.classList.add('rw-modal-open')

        // Scrolling verhindern
        document.body.style.overflow = 'hidden'

        return this
    }

    /**
     * Schließt das Modal
     * @returns {FeedbackModal} - Sich selbst für Method-Chaining
     */
    close() {
        // Modal ausblenden
        this.backdropElement.classList.remove('rw-modal-open')
        this.modalElement.classList.remove('rw-modal-open')

        // Verzögertes Ausblenden für Animation
        setTimeout(() => {
            this.backdropElement.style.display = 'none'

            // Scrolling wiederherstellen
            document.body.style.overflow = ''
        }, 300)

        return this
    }

    /**
     * Prüft, ob das Modal geöffnet ist
     * @returns {boolean} - True wenn Modal offen ist
     */
    isOpen() {
        return this.backdropElement.style.display !== 'none'
    }

    /**
     * Statische Factory-Methode zum Erstellen und Öffnen eines Modals
     * @param {Object} options - Modal-Optionen
     * @returns {FeedbackModal} - Die erstellte Modal-Instanz
     */
    static show(options) {
        const modal = new FeedbackModal(options)
        modal.open()
        return modal
    }
}
